import { useEffect, useRef } from 'react';
import '../styles/AsciiMark.css';

const CHARS = 'ABCDEF0123456789';
const ALPHA_ON = 100;

function randomChar() {
  return CHARS[Math.floor(Math.random() * CHARS.length)];
}

// The "b" is built as plain line art (strokes), not filled shapes:
//  - one straight vertical line (the stem)
//  - a squarish "C" bracket attached to it (the cover), open on the
//    left where it meets the stem
//  - two small circles inside the bracket
const SHAPE = {
  stemXR: 0.28, // x position of the stem, fraction of width
  stemTopR: 0.05,
  stemBottomR: 0.95,

  coverTopR: 0.48, // fraction of height — where the bracket starts
  coverBottomR: 0.93,
  coverRightR: 0.8, // fraction of width — right edge of the bracket
  coverCornerR: 0.05, // corner radius, fraction of width — keep this SMALL or the bracket rounds into a circle

  eye1CxR: 0.46,
  eye2CxR: 0.64,
  eyeCyR: 0.72,
  eyeRadiusR: 0.035, // fraction of height — keep small so the two circles stay distinct

  strokeWidthR: 0.015, // fraction of height
};

function paintGlyph(ctx, width, height) {
  const strokeWidth = height * SHAPE.strokeWidthR;
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Stem: one straight line
  const stemX = width * SHAPE.stemXR;
  ctx.beginPath();
  ctx.moveTo(stemX, height * SHAPE.stemTopR);
  ctx.lineTo(stemX, height * SHAPE.stemBottomR);
  ctx.stroke();

  // Cover: a squarish "C" bracket — top edge, rounded corner, right
  // edge, rounded corner, bottom edge. Open on the left (meets the stem).
  const top = height * SHAPE.coverTopR;
  const bottom = height * SHAPE.coverBottomR;
  const right = width * SHAPE.coverRightR;
  const r = width * SHAPE.coverCornerR;
  ctx.beginPath();
  ctx.moveTo(stemX, top);
  ctx.lineTo(right - r, top);
  ctx.arcTo(right, top, right, top + r, r);
  ctx.lineTo(right, bottom - r);
  ctx.arcTo(right, bottom, right - r, bottom, r);
  ctx.lineTo(stemX, bottom);
  ctx.stroke();

  // Two circles inside the cover
  const eyeR = height * SHAPE.eyeRadiusR;
  const eyeCy = height * SHAPE.eyeCyR;
  [width * SHAPE.eye1CxR, width * SHAPE.eye2CxR].forEach((cx) => {
    ctx.beginPath();
    ctx.arc(cx, eyeCy, eyeR, 0, Math.PI * 2);
    ctx.stroke();
  });
}

export default function AsciiMark({ className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let width = 0;
    let height = 0;
    let cells = [];

    function buildMask() {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, Math.round(rect.width));
      height = Math.max(1, Math.round(rect.height));
      canvas.width = width * dpr;
      canvas.height = height * dpr;

      const off = document.createElement('canvas');
      off.width = width;
      off.height = height;
      const octx = off.getContext('2d');
      octx.clearRect(0, 0, width, height);
      paintGlyph(octx, width, height);
      const { data } = octx.getImageData(0, 0, width, height);

      // The shape is already just a thin line, so we sample every
      // grid point that lands ON the stroke — no edge-detection needed.
      const step = Math.max(6, Math.round(width / 34));
      const found = [];
      for (let y = 0; y < height; y += step) {
        for (let x = 0; x < width; x += step) {
          const alpha = data[(y * width + x) * 4 + 3];
          if (alpha > ALPHA_ON) {
            found.push({ x, y, value: randomChar(), bright: Math.random() < 0.15 });
          }
        }
      }
      cells = found;
    }

    function draw() {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '11px "Courier New", monospace';
      cells.forEach((cell) => {
        ctx.fillStyle = cell.bright ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.45)';
        ctx.fillText(cell.value, cell.x, cell.y);
      });
    }

    function tick() {
      cells.forEach((cell) => {
        if (Math.random() < 0.06) cell.value = randomChar();
      });
      draw();
    }

    buildMask();
    draw();
    const interval = setInterval(tick, 130);

    function handleResize() {
      buildMask();
      draw();
    }
    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return <canvas ref={canvasRef} className={`ascii-mark ${className}`} aria-hidden="true" />;
}